import { ArrowLeft, Cross, ExternalLink, Loader } from "lucide-react";
import { SyntheticEvent } from "react";

export type ReactivButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "tertiaryLeft"
  | "textLink"
  | "return"
  | "icon"
  | "externalLink";
export type ReactivButtonColor = "black" | "red";

export type ReactivButtonProps = {
  // Base
  children?: React.ReactNode;
  /**
   * @param id \{`string=undefined`\} default html `id` prop.
   */
  id?: string;
  // Styling
  /**
   * @param size \{`string="md"`\} size of text and padding for variants that don't enforce specific sizes.
   */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * @param variant \{`string="secondary"`\} design-assigned style variant.
   */
  variant?: ReactivButtonVariant;
  /**
   * @param color \{`string="black"`\} design-assigned color scheme.
   */
  color?: ReactivButtonColor;
  // Custom styling
  /**
   * @param className \{`string=undefined`\} custom classes to be applied.
   */
  className?: string;
  /**
   * @param style \{`React.CSSProperties={}`\} custom styles to be applied.
   */
  style?: React.CSSProperties;
  // Type
  /**
   * @param type \{`string="button"`\} default html `type` prop.
   */
  type?: "button" | "submit" | "reset";
  /**
   * @param name \{`string=undefined`\} default html `name` prop.
   */
  name?: string;
  /**
   * @param value \{`string=undefined`\} default html `value` prop.
   */
  value?: string;
  // Actions
  /**
   * @param disabled \{`boolean=false`\} default html `disabled` prop.
   */
  disabled?: boolean;
  /**
   * @param loading \{`boolean=false`\} whether the button is considered loading; a loading button is also disabled.
   */
  loading?: boolean;
  /**
   *
   * @param onClick \{`(e: SyntheticEvent) => void = undefined`\} default html `onClick` prop.
   */
  onClick?: (e: SyntheticEvent) => void;
};

const REACTIV_BUTTON_SIZE_CLASSES = Object.freeze({
  xs: "px-2 py-1 text-xs",
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-sm",
  lg: "px-5 py-2.5 text-base",
  xl: "px-6 py-3 text-base",
});

type ButtonClassObj = {
  base?: string;
  variants?: { [variant: string]: ButtonClassObj };
  colors?: { [color: string]: ButtonClassObj };
};

const STYLE_CLASSES = Object.freeze<ButtonClassObj>({
  base:
    "relative flex items-center justify-center rounded-lg text-center min-h-[fit-content] min-w-[fit-content] " +
    "transition-[color,background-color,border-color,text-decoration-color,fill,stroke,box-shadow] focus:z-10 " +
    "enabled:cursor-pointer disabled:cursor-not-allowed",
  variants: {
    primary: {
      base: "text-white font-semibold enabled:hover:shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.1),0px_1px_3px_0px_rgba(0,0,0,0.1)]",
      colors: {
        black: {
          base:
            "bg-neutral-900 enabled:hover:bg-dark-hover-bg enabled:active:bg-dark-selected-bg enabled:focus:bg-dark-selected-bg " +
            "disabled:bg-dark-disabled-bg disabled:text-dark-disabled-text",
        },
        red: {
          base:
            "bg-red-default-bg enabled:hover:bg-red-hover-bg enabled:active:bg-red-selected-bg enabled:focus:bg-red-selected-bg " +
            "enabled:focus:outline enabled:focus:outline-2 enabled:focus:outline-black disabled:bg-red-disabled-bg disabled:text-red-disabled-text",
        },
      },
    },
    secondary: {
      base:
        "bg-white border enabled:hover:shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.1),0px_1px_3px_0px_rgba(0,0,0,0.1)] " +
        "enabled:focus:outline enabled:focus:outline-2 enabled:focus:outline-offset-[-1px] enabled:focus:outline-dark-selected-bg " +
        "disabled:border-transparent font-semibold",
      colors: {
        black: {
          base:
            "text-neutral-900 border-[#CBD3D6] enabled:hover:text-dark-hover-bg enabled:active:text-dark-selected-bg " +
            "enabled:active:bg-[#F4F6F7] enabled:focus:text-dark-selected-bg disabled:text-dark-disabled-text disabled:bg-dark-disabled-bg",
        },
        red: {
          base:
            "text-red-default-bg border-[#FFC1CC] enabled:hover:text-red-hover-bg enabled:active:text-red-selected-bg " +
            "enabled:active:bg-[#FFC1CC] enabled:focus:text-red-selected-bg disabled:text-red-disabled-text disabled:bg-red-disabled-bg",
        },
      },
    },
    tertiary: {
      base:
        "font-semibold enabled:hover:bg-light-hover-bg enabled:active:bg-light-selected-bg enabled:focus:outline " +
        "enabled:focus:outline-2 enabled:focus:outline-dark-selected-bg",
      colors: {
        black: {
          base:
            "text-neutral-900 enabled:hover:text-dark-hover-bg enabled:active:text-dark-selected-bg " +
            "enabled:focus:text-dark-selected-bg disabled:text-dark-disabled-text",
        },
        red: {
          base:
            "text-red-default-bg enabled:hover:text-red-hover-bg enabled:active:text-red-selected-bg " +
            "enabled:focus:text-red-selected-bg disabled:text-red-disabled-text",
        },
      },
    },
    tertiaryLeft: {
      base:
        "font-semibold enabled:hover:bg-light-hover-bg enabled:active:bg-light-selected-bg enabled:focus:outline " +
        "enabled:focus:outline-2 enabled:focus:outline-dark-selected-bg !p-[3px]",
      colors: {
        black: {
          base:
            "text-neutral-900 enabled:hover:text-dark-hover-bg enabled:active:text-dark-selected-bg " +
            "enabled:focus:text-dark-selected-bg disabled:text-dark-disabled-text",
        },
        red: {
          base:
            "text-red-default-bg enabled:hover:text-red-hover-bg enabled:active:text-red-selected-bg " +
            "enabled:focus:text-red-selected-bg disabled:text-red-disabled-text",
        },
      },
    },
    textLink: {
      base: "!p-[3px] font-normal underline enabled:focus:outline enabled:focus:outline-2 enabled:focus:outline-dark-selected-bg",
      colors: {
        black: {
          base:
            "text-neutral-900 enabled:hover:text-dark-hover-bg enabled:active:text-dark-selected-bg enabled:focus:text-dark-selected-bg " +
            "disabled:text-dark-disabled-text",
        },
        red: {
          base:
            "text-red-default-bg enabled:hover:text-red-hover-bg enabled:active:text-red-selected-bg enabled:focus:text-red-selected-bg " +
            "disabled:text-red-disabled-text",
        },
      },
    },
    return: {
      base:
        "!p-[3px] font-semibold enabled:hover:bg-light-hover-bg enabled:hover:shadow-[0px_0px_8px_0px_rgba(37,39,44,0.2)] " +
        "enabled:active:bg-light-selected-bg enabled:focus:outline enabled:focus:outline-2 enabled:focus:outline-dark-selected-bg",
      colors: {
        black: {
          base:
            "text-neutral-900 enabled:hover:text-dark-hover-bg enabled:active:text-dark-selected-bg " +
            "enabled:focus:text-dark-selected-bg disabled:text-dark-disabled-text",
        },
        red: {
          base:
            "text-red-default-bg enabled:hover:text-red-hover-bg enabled:active:text-red-selected-bg " +
            "enabled:focus:text-red-selected-bg disabled:text-red-disabled-text",
        },
      },
    },
    icon: {
      base:
        "!p-1 !rounded-full enabled:hover:bg-light-hover-bg enabled:hover:shadow-[0px_0px_8px_0px_rgba(37,39,44,0.2)] " +
        "enabled:active:bg-light-selected-bg enabled:focus:outline enabled:focus:outline-2 enabled:focus:outline-dark-selected-bg " +
        "disabled:text-dark-disabled-text",
    },
    externalLink: {
      base:
        "!p-[3px] font-semibold enabled:hover:bg-light-hover-bg enabled:hover:shadow-[0px_0px_8px_0px_rgba(37,39,44,0.2)] " +
        "enabled:active:bg-light-selected-bg enabled:focus:outline enabled:focus:outline-2 enabled:focus:outline-dark-selected-bg",
      colors: {
        black: {
          base:
            "text-neutral-900 enabled:hover:text-dark-hover-bg enabled:active:text-dark-selected-bg " +
            "enabled:focus:text-dark-selected-bg disabled:text-dark-disabled-text",
        },
        red: {
          base:
            "text-red-default-bg enabled:hover:text-red-hover-bg enabled:active:text-red-selected-bg " +
            "enabled:focus:text-red-selected-bg disabled:text-red-disabled-text",
        },
      },
    },
  },
});

/**
 * Internal Reactiv button
 * @param size \{`string="md"`\} size of text and padding for variants that don't enforce specific sizes.
 * @param variant \{`string="secondary"`\} design-assigned style variant.
 * @param color \{`string="black"`\} design-assigned color scheme.
 * @param loading \{`boolean=false`\} whether the button is considered loading; a loading button is also disabled.
 * @param onClick \{`(e: SyntheticEvent) => void = undefined`\} default html `onClick` prop.
 * All standard HTML button attributes are also supported (id, className, disabled, type, name, value, formNoValidate, aria-*, etc.)
 */
export const ReactivButton = ({
  // Styling
  size = "md",
  variant = "secondary",
  color = "black",
  // Custom styling
  className = undefined,
  // Actions
  loading = false,
  disabled = false,
  onClick = undefined,
  children,
  type = "button",
  ...restProps
}: ReactivButtonProps) => {
  // Build className safely (no "undefined") and allow consumer overrides last
  const computedClassName = [
    REACTIV_BUTTON_SIZE_CLASSES[size],
    STYLE_CLASSES.base,
    STYLE_CLASSES.variants?.[variant]?.base,
    STYLE_CLASSES.variants?.[variant]?.colors?.[color]?.base,
    loading && "!cursor-wait",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={computedClassName}
      {...restProps}
    >
      {variant === "return" ? <ArrowLeft className="mr-1" /> : undefined}
      {children ?? undefined}
      {variant === "externalLink" ? (
        <ExternalLink className="ml-1 inline" />
      ) : undefined}
      {loading ? <Loader className="h-6 w-6 animate-spin ml-1" /> : undefined}
    </button>
  );
};

export const ReactivButtonTestComponent = () => {
  return (
    <div>
      <div className="flex gap-6 m-6 items-center">
        <ReactivButton variant="primary" color="black">
          Hi
        </ReactivButton>
        <ReactivButton variant="secondary" color="black">
          Hi
        </ReactivButton>
        <ReactivButton variant="tertiary" color="black">
          Hi
        </ReactivButton>
        <ReactivButton variant="tertiaryLeft" color="black">
          Hi
        </ReactivButton>
        <ReactivButton variant="textLink" color="black">
          Hi
        </ReactivButton>
        <ReactivButton variant="return" color="black">
          Hi
        </ReactivButton>
        <ReactivButton variant="icon" color="black">
          <Cross />
        </ReactivButton>
        <ReactivButton variant="externalLink" color="black" loading={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="externalLink" color="black">
          Hi
        </ReactivButton>
      </div>
      <div className="flex gap-6 m-6 items-center">
        <ReactivButton variant="primary" color="red">
          Hi
        </ReactivButton>
        <ReactivButton variant="secondary" color="red">
          Hi
        </ReactivButton>
        <ReactivButton variant="tertiary" color="red">
          Hi
        </ReactivButton>
        <ReactivButton variant="tertiaryLeft" color="red">
          Hi
        </ReactivButton>
        <ReactivButton variant="textLink" color="red">
          Hi
        </ReactivButton>
        <ReactivButton variant="return" color="red">
          Hi
        </ReactivButton>
        <ReactivButton variant="icon" color="red">
          H
        </ReactivButton>
        <ReactivButton variant="externalLink" color="red" loading={true}>
          Hi
        </ReactivButton>
      </div>
      <div className="flex gap-6 m-6 items-center">
        <ReactivButton variant="primary" color="black" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="secondary" color="black" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="tertiary" color="black" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="tertiaryLeft" color="black" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="textLink" color="black" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="return" color="black" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="icon" color="black" disabled={true}>
          H
        </ReactivButton>
        <ReactivButton variant="externalLink" color="black" loading={true}>
          Hi
        </ReactivButton>
      </div>
      <div className="flex gap-6 m-6 items-center">
        <ReactivButton variant="primary" color="red" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="secondary" color="red" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="tertiary" color="red" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="tertiaryLeft" color="red" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="textLink" color="red" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="return" color="red" disabled={true}>
          Hi
        </ReactivButton>
        <ReactivButton variant="icon" color="red" disabled={true}>
          H
        </ReactivButton>
        <ReactivButton variant="externalLink" color="red" loading={true}>
          Hi
        </ReactivButton>
      </div>
    </div>
  );
};
